import { takeEvery, takeLatest } from 'redux-saga'
import { put, take, select, race } from 'redux-saga/effects'

import actionTypes from 'redux/actions/actionTypes'
import { drawItSelectors } from 'redux/selectors'
import { drawItSync, iconSync, slideSync } from 'sync'
import fileUploader from 'helpers/fileUploader'
import callAny from './helpers/call_any'
import { localize } from 'helpers/translator'
import errorCodes from 'helpers/enums/errorCodes'
import iconTypes from 'helpers/enums/iconTypes'
import drawItActions from 'redux/actions/drawItActions'
import drawItTimerSelector from 'redux/selectors/draw_it/draw_it_timer_selector'

function *drawItCreateInitialize(action) {
	if (!action.drawItUID) {
		return
	}
	try {
		const response = yield callAny(drawItSync.getDrawIt, action.drawItUID)
		yield put({
			type: actionTypes.DRAW_IT_INITIALIZE_SUCCESS,
			data: response.payload.draw_it,
		})
	} catch (error) {
		yield put({
			type: actionTypes.DRAW_IT_INITIALIZE_ERROR,
		})
	}
}

function *drawItSaveDrawIt() {
	try {
		const errorActions = [
			actionTypes.DRAW_IT_UPLOADER_VALIDATE_ERROR,
			actionTypes.DRAW_IT_VALIDATE_BACKGROUND_IMAGE_ERROR,
			actionTypes.DRAW_IT_UPLOAD_REFERENCE_MEDIA_ERROR,
			actionTypes.DRAW_IT_UPLOADER_UPLOAD_ERROR,
			actionTypes.DRAW_IT_UPLOADER_VALIDATE_CONTENT_ERROR,
			actionTypes.DRAW_IT_UPLOAD_BACKGROUND_IMAGE_ERROR,
			actionTypes.PROCESSING_ICON_ERROR, //@todo: hmike: validate if icon belongs to Draw It
		]
		const successActions = [
			actionTypes.DRAW_IT_SAVE_DRAW_IT_SUCCESS,
			actionTypes.DRAW_IT_UPLOADER_UPLOAD_SUCCESS,
			actionTypes.DRAW_IT_UPLOAD_BACKGROUND_IMAGE_SUCCESS,
		]
		let saveSelector = yield select(drawItSelectors.saveSelector)
		yield put({
			type: actionTypes.DRAW_IT_VALIDATION_SUCCESS,
			text: saveSelector.allIconsDone?null:localize('draw_it_will_save_after_uploading'),
		})
		while (!saveSelector.allIconsDone) {
			const requestActions = yield take([...successActions, ...errorActions])
			if (errorActions.includes(requestActions.type)) {
				throw true
			} else {
				saveSelector = yield select(drawItSelectors.saveSelector)
			}
		}
		if (saveSelector.allIconsDone) {
			const response = yield callAny(
				drawItSync.saveDrawIt,
				saveSelector.entityUID,
				saveSelector.instructions.text,
				saveSelector.iconId,
				saveSelector.iconBackgroundId,
				saveSelector.presentationId,
				saveSelector.isFlippedHorizontally,
				saveSelector.isFlippedVertically,
				saveSelector.scale,
				saveSelector.rotatedDegrees,
				saveSelector.isModified,
				true,
				saveSelector.offsetX,
				saveSelector.offsetY,
			)
			yield put({
				type: actionTypes.DRAW_IT_SAVE_DRAW_IT_SUCCESS,
				redirectUrl: response.payload.redirectUrl,
				metrics: response.payload.metrics,
			})
		}

	} catch (error) {
		yield put({
			type: actionTypes.DRAW_IT_SAVE_DRAW_IT_ERROR,
			message: localize('global_generic_error'),
		})
	}
}

function *drawItSaveInstructions() {
	try {
		const saveSelector = yield select(drawItSelectors.saveSelector)
		yield callAny(
			drawItSync.saveDrawIt,
			saveSelector.entityUID,
			saveSelector.instructions.text,
			saveSelector.iconId,
			saveSelector.iconBackgroundId
		)
		yield put({
			type: actionTypes.DRAW_IT_SAVE_INSTRUCTIONS_SUCCESS,
		})
	} catch (error) {
		yield put({
			type: actionTypes.DRAW_IT_SAVE_INSTRUCTIONS_ERROR,
			message: localize('global_generic_error'),
		})
	}
}

function *drawItUploadReferenceMedia(action) {
	try {
		const drawItSelector = yield select(drawItSelectors.drawItUploadReferenceMediaSelector)
		const validateResponse = yield callAny(
			iconSync.validateFilesPicked,
			action.allowedTypes,
			action.files['files'],
			drawItSelector.presentationId,
			drawItSelector.entityUID,
			action.force
		)
		const payload = validateResponse.payload.icons[0]
		const file = action.files['files'][0]
		if (payload.error_code === 0) {
			yield put(drawItActions.validateReferenceMediaSuccess(payload.uploadId))
			const {uploadResponse} = yield race({
				iconDeleted: take([actionTypes.DRAW_IT_DELETE_REFERENCE_MEDIA]),
				uploadResponse: callAny(
					fileUploader,
					payload.uploadId,
					file,
					'Slide',
					drawItSelector.entityUID,
					iconTypes.ENTITY_ICON_TYPE_REFERENCE_MEDIA,
					action.allowedTypes,
					drawItSelector.presentationId,
					drawItSelector.entityUID,
					action.force
				),
			})
			if (uploadResponse) {
				yield put({
					type: actionTypes.DRAW_IT_UPLOADER_UPLOAD_SUCCESS,
					icon: uploadResponse.payload.icon,
				})
			}
		} else {
			// webpage content validation
			if (payload.error_code === errorCodes.ICON_INVALID_CONTENT_INTEGRITY) {
				yield put({
					type: actionTypes.DRAW_IT_UPLOADER_VALIDATE_CONTENT_ERROR,
					message: payload.message,
				})
				yield take(actionTypes.DRAW_IT_UPLOAD_REQUIRE_CONFIRMATION)
				yield put({
					type: actionTypes.DRAW_IT_UPLOAD_REFERENCE_MEDIA,
					allowedTypes: action.allowedTypes,
					files: action.files,
					featureType: action.featureType,
					force: true,
				})
			} else {
				yield put({
					type: actionTypes.DRAW_IT_UPLOADER_VALIDATE_ERROR,
					message: payload.message,
				})
			}
		}
	} catch (error) {
		let message = localize('quiz_service_error_message')
		if (error.serverError.message) {
			message = error.serverError.message
		}
		yield put({
			type: actionTypes.DRAW_IT_UPLOADER_UPLOAD_ERROR,
			error: error.serverError,
			message,
		})
	}
}

function *drawItUploadBackgroundImage(action) {
	try {
		const drawItSelector = yield select(drawItSelectors.drawItUploadBackgroundImageSelector)
		const validateResponse = yield callAny(
			iconSync.validateFilesPicked,
			action.allowedTypes,
			action.files['files'],
			drawItSelector.presentationId,
			drawItSelector.entityUID,
			action.force
		)
		const payload = validateResponse.payload.icons[0]
		const file = action.files['files'][0]
		if (payload.error_code === 0) {
			yield put(drawItActions.validateBackgroundImageSuccess(payload.uploadId))
			const {uploadResponse} = yield race({
				iconDeleted: take([actionTypes.DRAW_IT_DELETE_REFERENCE_MEDIA]),
				uploadResponse: callAny(
					fileUploader,
					payload.uploadId,
					file,
					'Slide',
					drawItSelector.entityUID,
					iconTypes.ENTITY_ICON_TYPE_DEFAULT,
					action.allowedTypes,
					drawItSelector.presentationId,
					drawItSelector.entityUID,
					action.force
				),
			})
			if (uploadResponse) {
				yield put({
					type: actionTypes.DRAW_IT_UPLOAD_BACKGROUND_IMAGE_SUCCESS,
					icon: uploadResponse.payload.icon,
				})
			}
		} else {
			yield put({
				type: actionTypes.DRAW_IT_VALIDATE_BACKGROUND_IMAGE_ERROR,
				message: payload.message,
			})
		}
	} catch (error) {
		let message = localize('quiz_service_error_message')
		if (error.serverError.message) {
			message = error.serverError.message
		}
		yield put({
			type: actionTypes.DRAW_IT_UPLOAD_BACKGROUND_IMAGE_ERROR,
			error: error.serverError,
			message,
		})
	}
}

function *drawItCancelChanges() {
	const cancelSelector = yield select(drawItSelectors.drawItCancelSelector)
	if (cancelSelector.isModified) {
		yield put({
			type: actionTypes.DRAW_IT_CANCEL_DRAW_IT_CONFIRM,
		})
	} else {
		yield put({
			type: actionTypes.DRAW_IT_CANCEL_DRAW_IT_CONFIRMED,
		})
	}
}

function *drawItDeleteReferenceMedia() {
	try {
		const deleteReferenceMediaSelector = yield select(drawItSelectors.deleteReferenceMediaSelector)
		yield put(drawItActions.deleteReferenceMediaSuccess())
		if (deleteReferenceMediaSelector.icon.status === 'uploading') {
			yield callAny(iconSync.cancelUpload, deleteReferenceMediaSelector.icon.uploadId)
		}
	} catch (error) {
		// console.log(error)
	}
}

function *timerSave(action) {
	try {
		const { timerValue, entityUID } = yield select(drawItTimerSelector)
		yield callAny(slideSync.saveTimer, timerValue, entityUID)
		yield put(drawItActions.saveTimerSuccess())
	} catch (error) {
		yield put(drawItActions.saveTimerError(action.previous_timer))
	}
}

export default [
	takeLatest([
		actionTypes.DRAW_IT_INITIALIZE_CREATION_TOOL,
		actionTypes.DRAW_IT_INITIALIZE_STANDALONE,
		actionTypes.QUICK_LAUNCH_DRAW_IT_INITIALIZE_CREATION_TOOL,
		actionTypes.QUICK_LAUNCH_DRAW_IT_CREATE_LESSON_SUCCESS,
		actionTypes.DRAW_IT_UPDATE_CREATION_TOOL,
	], drawItCreateInitialize),
	takeLatest(actionTypes.DRAW_IT_SAVE_DRAW_IT, drawItSaveDrawIt),
	takeLatest([
		actionTypes.DRAW_IT_SAVE_INSTRUCTIONS,
		actionTypes.DRAW_IT_DELETE_REFERENCE_MEDIA_SUCCESS,
		actionTypes.DRAW_IT_DELETE_BACKGROUND_IMAGE_SUCCESS,
	], drawItSaveInstructions),
	takeEvery(actionTypes.DRAW_IT_UPLOAD_REFERENCE_MEDIA, drawItUploadReferenceMedia),
	takeEvery(actionTypes.DRAW_IT_UPLOAD_BACKGROUND_IMAGE, drawItUploadBackgroundImage),
	takeLatest(actionTypes.DRAW_IT_CANCEL_DRAW_IT, drawItCancelChanges),
	takeLatest([
		actionTypes.DRAW_IT_DELETE_REFERENCE_MEDIA,
		actionTypes.DRAW_IT_AUDIO_PLAYER_DELETE_CONFIRMED,
	], drawItDeleteReferenceMedia),
	takeLatest([
		actionTypes.DRAW_IT_SAVE_TIMER,
		actionTypes.DRAW_IT_DELETE_TIMER,
	], timerSave),
]

export {
	drawItCreateInitialize,
	drawItSaveDrawIt,
	drawItCancelChanges,
	drawItDeleteReferenceMedia,
	drawItSaveInstructions,
	timerSave,
}
